import { useEffect, useState } from "react";
import { toast } from "sonner";
import { generateYouTubeKit, generateImage, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  X, Sparkle, Copy, Image as ImageIcon, FilmReel, Hash, TextT, ArrowsClockwise, Lock,
} from "@phosphor-icons/react";

function CopyBtn({ text, testId }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-slate-400 hover:text-slate-900 transition-colors"
      data-testid={testId}
    >
      <Copy size={12} weight="bold" /> {done ? "Copied" : "Copy"}
    </button>
  );
}

function ImagePromptCard({ prompt, index, aiEnabled }) {
  const [img, setImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    if (!aiEnabled) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await generateImage(prompt);
      setImg(res.image);
      toast.success(`Image ${index + 1} ${res.cached ? "loaded from cache" : "generated"}`);
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(`Image generation failed`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-slate-200 bg-slate-50 p-4 rounded-xl" data-testid={`image-prompt-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Image Prompt #{index + 1}
        </span>
        <CopyBtn text={prompt} testId={`copy-image-prompt-${index}`} />
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mb-3">{prompt}</p>

      {img && (
        <div className="border border-slate-200 mb-3 rounded-lg overflow-hidden">
          <img src={img} alt={`Generated ${index + 1}`} className="w-full" data-testid={`generated-image-${index}`} />
        </div>
      )}

      {err && <p className="text-xs text-red-500 mb-2">{err}</p>}
    </div>
  );
}

export default function GeneratorPanel({ item, onClose }) {
  const { settings } = useAuth();
  const aiEnabled = settings?.ai_enabled !== false;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("script");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    generateYouTubeKit({
      news_id: item.id,
      title: item.title,
      summary: item.summary || "",
      source: item.source || "",
      link: item.link || "",
      category: item.category || "top",
    })
      .then((d) => {
        if (alive) {
          setData(d);
          toast.success("Content kit ready");
        }
      })
      .catch((e) => {
        if (alive) {
          const status = e?.response?.status;
          const msg = formatApiError(e);
          setError(msg);
          if (status === 429) {
            toast.error("Daily limit reached", { description: "Upgrade to Premium for unlimited generations." });
          } else {
            toast.error("Generation failed");
          }
        }
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [item]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const tabs = [
    { id: "script", label: "Script",  icon: TextT },
    { id: "images", label: "Images",  icon: ImageIcon },
    { id: "videos", label: "Video",   icon: FilmReel },
    { id: "meta",   label: "Meta",    icon: Hash },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="generator-panel-overlay"
    >
      <aside
        className="w-full md:w-[640px] h-full bg-white border-l border-slate-200 overflow-y-auto shadow-2xl"
        data-testid="generator-panel"
      >
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
              <Sparkle size={16} weight="fill" color="#fff" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Content Kit
              </div>
              <div className="font-semibold text-sm text-slate-900 truncate">{item.title}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500"
            data-testid="close-generator-panel"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center" data-testid="generator-loading">
            <ArrowsClockwise size={28} className="animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Generating your kit…
            </p>
            <p className="text-xs text-slate-400 mt-2">~10–20 seconds</p>
          </div>
        )}

        {error && (
          <div className="p-6">
            <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-600 rounded-xl" data-testid="generator-error">
              {error}
            </div>
          </div>
        )}

        {data && (
          <>
            <div className="flex border-b border-slate-200 sticky top-[73px] z-10 bg-white" data-testid="generator-tabs">
              {tabs.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs font-medium uppercase tracking-wider border-b-2 transition-colors ${
                      active ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}
                    data-testid={`tab-${t.id}`}
                  >
                    <Icon size={14} weight="bold" /> {t.label}
                  </button>
                );
              })}
            </div>

            <div className="p-6 space-y-6">
              {tab === "script" && (
                <div className="space-y-5" data-testid="script-tab">
                  {data.script?.hook && (
                    <Section title="Hook" testId="section-hook" copyText={data.script.hook}>
                      <p className="text-base md:text-lg text-slate-900 font-semibold leading-snug">{data.script.hook}</p>
                    </Section>
                  )}
                  {data.script?.intro && (
                    <Section title="Intro" testId="section-intro" copyText={data.script.intro}>
                      <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{data.script.intro}</p>
                    </Section>
                  )}
                  {data.script?.body && (
                    <Section title="Body" testId="section-body" copyText={data.script.body}>
                      <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{data.script.body}</p>
                    </Section>
                  )}
                  {data.script?.cta && (
                    <Section title="Call-to-action" testId="section-cta" copyText={data.script.cta}>
                      <p className="text-sm text-slate-600 leading-relaxed">{data.script.cta}</p>
                    </Section>
                  )}
                  {data.script?.full && (
                    <Section title="Full script" testId="section-full" copyText={data.script.full}>
                      <p className="text-sm text-slate-500 leading-relaxed whitespace-pre-line">{data.script.full}</p>
                    </Section>
                  )}
                </div>
              )}

              {tab === "images" && (
                <div className="space-y-4" data-testid="images-tab">
                  {(data.image_prompts || []).map((p, i) => (
                    <ImagePromptCard key={i} prompt={p} index={i} aiEnabled={aiEnabled} />
                  ))}
                </div>
              )}

              {tab === "videos" && (
                <div className="space-y-3" data-testid="videos-tab">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    B-roll &amp; video clip prompts
                  </p>
                  {(data.video_prompts || []).map((p, i) => (
                    <div key={i} className="border border-slate-200 bg-slate-50 p-4 rounded-xl" data-testid={`video-prompt-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Clip #{i + 1}</span>
                        <CopyBtn text={p} testId={`copy-video-prompt-${i}`} />
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
                    </div>
                  ))}
                </div>
              )}

              {tab === "meta" && (
                <div className="space-y-5" data-testid="meta-tab">
                  {data.title_suggestions?.length > 0 && (
                    <Section title="Title suggestions" testId="section-titles">
                      <ul className="space-y-2">
                        {data.title_suggestions.map((t, i) => (
                          <li key={i} className="flex items-start justify-between gap-3 border border-slate-200 p-3 bg-slate-50 rounded-lg" data-testid={`title-suggestion-${i}`}>
                            <span className="text-sm text-slate-800">{t}</span>
                            <CopyBtn text={t} testId={`copy-title-${i}`} />
                          </li>
                        ))}
                      </ul>
                    </Section>
                  )}
                  {data.thumbnail_concept && (
                    <Section title="Thumbnail concept" testId="section-thumbnail" copyText={data.thumbnail_concept}>
                      <p className="text-sm text-slate-600 leading-relaxed">{data.thumbnail_concept}</p>
                    </Section>
                  )}
                  {data.hashtags?.length > 0 && (
                    <Section title="Hashtags" testId="section-hashtags" copyText={data.hashtags.map((h) => `#${h}`).join(" ")}>
                      <div className="flex flex-wrap gap-2">
                        {data.hashtags.map((h, i) => (
                          <span key={i} className="text-xs font-medium px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-full text-slate-700" data-testid={`hashtag-${i}`}>
                            #{h}
                          </span>
                        ))}
                      </div>
                    </Section>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function Section({ title, children, copyText, testId }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{title}</span>
        {copyText && <CopyBtn text={copyText} testId={`${testId}-copy`} />}
      </div>
      <div className="p-4 bg-white">{children}</div>
    </div>
  );
}
